/**
 * MerchantSwitcher — active-merchant dropdown for multi-merchant users.
 *
 * Renders nothing for users with a single membership (i.e. essentially all
 * existing merchants), so it is invisible unless a user actually belongs to
 * more than one merchant. Selecting a merchant re-scopes the whole app via
 * AuthContext.setCurrentMerchant (which persists the choice and clears
 * per-merchant filter state).
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Store } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function MerchantSwitcher() {
  const { merchantId, availableMerchants, setCurrentMerchant } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Only meaningful when the user belongs to more than one merchant.
  if (availableMerchants.length <= 1) return null;

  const active = availableMerchants.find((m) => m.id === merchantId);

  return (
    <div style={{ position: 'relative' }} ref={ref} data-testid="merchant-switcher">
      {/* Compact Store-icon button on mobile; icon + name on wider screens.
          Uses its own class (not lang-selector, which is hidden below 640px). */}
      <button
        className="merchant-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        title={`Switch merchant${active?.name ? ` (current: ${active.name})` : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch merchant"
      >
        <Store className="w-5 h-5" />
        <span className="merchant-switcher-label">{active?.name ?? 'Select merchant'}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="listbox"
          style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', minWidth: '220px', zIndex: 50 }}
          className="bg-primary border border-primary rounded-lg shadow-lg overflow-hidden"
        >
          {availableMerchants.map((m) => {
            const isActive = m.id === merchantId;
            return (
              <button
                key={m.id}
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setCurrentMerchant(m.id);
                  setOpen(false);
                }}
                className={`w-full text-left px-md py-sm text-sm flex items-center justify-between gap-sm transition-colors ${
                  isActive ? 'bg-blue text-white' : 'text-primary hover:bg-secondary'
                }`}
              >
                <span className="truncate">{m.name}</span>
                {isActive && <Check className="w-4 h-4 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
